import { defineStore } from 'pinia';
import { makeAPIMap } from './makeAPIMap';

/**
 * A single archive-aware getter definition.
 * - role 'data' reads [dataKey] / [archive.archivedDataKey]
 * - role 'map' reads [mapKey] / [archive.archivedMapKey]
 * - mode 'notNull' (default): archived branch taken when `state.archived` is true AND the archived value is
 *   not null/undefined; falls through to the live value if it is not null/undefined; else `fallback`.
 * - mode 'truthy': archived branch taken when `state.archived` is true AND the archived value is truthy
 *   (matches a couple of old stores that used a plain `&&` check, which is a no-op once state has been
 *   fetched at least once since arrays/objects are always truthy — preserved verbatim, bug and all).
 * A bare 'data' | 'map' string is shorthand for { role, mode: 'notNull', fallback: undefined }.
 *
 * `fallback` is a FACTORY, not a literal (e.g. `() => ({ records: [] })` / `() => []`) — a plain literal
 * would be a single module-level object shared by reference across every request in a Worker isolate, so
 * any caller that mutated a returned fallback (e.g. pushed into it) would leak that mutation into every
 * subsequent request. The factory is invoked fresh on each getter read instead.
 */
export type ArchiveGetterSpec =
    | 'data'
    | 'map'
    | {
          role: 'data' | 'map';
          fallback?: () => any;
          mode?: 'truthy' | 'notNull';
      };

export interface CollectionStoreConfig {
    /** Pinia store id — unchanged from the old store. */
    id: string;
    /** Collection endpoint, e.g. '/api/sites'. */
    endpoint: string;
    /** State key holding the primary data (e.g. 'sites', 'events', 'data'). */
    dataKey: string;
    /** State holds `{ records: [...] }` instead of the plain array/value (events/tours/sponsors/partners). */
    wrapRecords?: boolean;
    /** State key holding the id map, if the store has one. */
    mapKey?: string;
    /** When set, fetch actions assign `res.data || dataFallback` instead of a bare `res.data`. */
    dataFallback?: any;
    /** When set, fetch actions assign `res.map || mapFallback` instead of a bare `res.map`. */
    mapFallback?: any;
    /** Whether this store tracks `lastFetched` (only sites/events/archives did, historically). */
    trackLastFetched?: boolean;
    /** Exact console.log label used by the old store's catch blocks, e.g. 'Fetch Sites Error: '. */
    errorLabel: string;
    /** Fetch action name, e.g. 'fetchSites'. */
    fetchName: string;
    archive?: {
        /** Toggle action name, e.g. 'updateSites' / 'toggleArchivedEvents'. */
        toggleName: string;
        /** State key holding the archived twin's data. */
        archivedDataKey: string;
        /** Archived fetch action name, e.g. 'fetchArchivedSites'. */
        archivedFetchName: string;
        /** Verbatim URL builder from the old store — many hit dead `/api/archive/<year>/...` routes. */
        archivedEndpoint: (year: any) => string;
        /** State key holding the archived twin's id map. If omitted but `mapKey` is set, the archived
         *  fetch writes into the *shared* `mapKey` (matches trails, which has one map for both). */
        archivedMapKey?: string;
        /** Overrides `errorLabel` for the archived fetch's catch block, when it differs (sponsors' bug). */
        errorLabel?: string;
        /** Archive-aware getters, e.g. `{ sitesData: 'data', sitesMap: 'map' }`. */
        getters?: Record<string, ArchiveGetterSpec>;
    };
    /** fetchXById-style action, verbatim URL builder. Extra args (e.g. events' `isArchived`) pass through. */
    byId?: {
        actionName: string;
        endpoint: (id: string, ...rest: any[]) => string;
    };
    /** Arbitrary named getters that don't fit the archive-aware pattern (non-archive stores, oddly-named
     *  getters, or getters that read state under a different name than dataKey/mapKey). */
    getters?: Record<string, (state: any) => any>;
    /** Anything the old store had beyond the pattern — merged into state last, so it can override defaults
     *  (e.g. an initial value of `null` where the generic default would be `[]`). */
    extraState?: () => Record<string, any>;
    /** Anything the old store had beyond the pattern — merged into actions last, so an entry here with the
     *  same name as a generated action (e.g. `fetchName`) fully replaces it. */
    extraActions?: Record<string, (...args: any[]) => any>;
}

function readArchiveGetter(state: any, mainKey: string, archivedKey: string | undefined, fallbackFactory: (() => any) | undefined, mode: 'truthy' | 'notNull') {
    const main = state[mainKey];
    const archived = archivedKey ? state[archivedKey] : undefined;

    if (mode === 'truthy') {
        if (state.archived && archived) return archived || fallbackFactory?.();
        return main || fallbackFactory?.();
    }

    // 'notNull' mode
    if (state.archived && archived !== null && archived !== undefined) return archived;
    if (main !== null && main !== undefined) return main;
    return fallbackFactory?.();
}

function buildArchiveGetters(cfg: CollectionStoreConfig): Record<string, (state: any) => any> {
    const getters: Record<string, (state: any) => any> = {};

    if (cfg.archive?.getters) {
        for (const [name, rawSpec] of Object.entries(cfg.archive.getters)) {
            const spec = typeof rawSpec === 'string' ? { role: rawSpec } : rawSpec;
            const mode = spec.mode ?? 'notNull';
            const mainKey = spec.role === 'map' ? cfg.mapKey! : cfg.dataKey;
            const archivedKey = spec.role === 'map' ? cfg.archive!.archivedMapKey : cfg.archive!.archivedDataKey;

            getters[name] = (state: any) => readArchiveGetter(state, mainKey, archivedKey, spec.fallback, mode);
        }
    }

    if (cfg.getters) {
        Object.assign(getters, cfg.getters);
    }

    return getters;
}

function buildState(cfg: CollectionStoreConfig): Record<string, any> {
    const state: Record<string, any> = {};

    state[cfg.dataKey] = cfg.wrapRecords ? null : [];
    if (cfg.mapKey) state[cfg.mapKey] = {};

    if (cfg.archive) {
        state[cfg.archive.archivedDataKey] = cfg.wrapRecords ? null : [];
        if (cfg.archive.archivedMapKey) state[cfg.archive.archivedMapKey] = {};
        state.archived = false;
    }

    if (cfg.trackLastFetched) state.lastFetched = null;

    // Sanctioned additive state — every rewritten store gains `version` from `res.meta?.version` (Task 10 uses it).
    state.version = null;

    return { ...state, ...(cfg.extraState?.() ?? {}) };
}

// Store id -> its config. Populated by defineCollectionStore() below. Lets hydrateCollectionStore()
// (Task 10's bootstrap hydration) look up a store's dataKey/mapKey/wrapRecords/trackLastFetched shape
// from just the store instance, so callers (the SSR/client plugin, the refresh composable) never need
// to know per-collection envelope semantics themselves.
const registry = new Map<string, CollectionStoreConfig>();

/** Same envelope a collectionHandler/`/api/bootstrap` entry returns: `{ data, map, meta: { version } }`. */
function applyCollectionResult(store: any, cfg: CollectionStoreConfig, res: any) {
    const patch: Record<string, any> = {};
    patch[cfg.dataKey] = cfg.wrapRecords ? { records: res?.data } : cfg.dataFallback !== undefined ? res?.data || cfg.dataFallback : res?.data;
    if (cfg.mapKey) {
        // Derived client-side from `data` rather than read off `res.map` — `/api/bootstrap` no longer
        // sends a map (it was ~50% of the wire size, a near-duplicate of `data`). Endpoints that still
        // send one (e.g. plain collectionHandler routes) have it ignored here; harmless, just redundant
        // bytes on those non-bootstrap paths.
        patch[cfg.mapKey] = Array.isArray(res?.data) ? makeAPIMap(res.data) : (cfg.mapFallback !== undefined ? cfg.mapFallback : {});
    }
    if (cfg.trackLastFetched) patch.lastFetched = Date.now();
    patch.version = res?.meta?.version ?? null;
    store.$patch(patch);
}

/**
 * Hydrate a collection store from a bootstrap-shaped payload (the per-collection value inside
 * `GET /api/bootstrap`'s `collections` map — identical shape to a plain `collectionHandler` response).
 * The single place that knows dataKey/mapKey/wrapRecords mapping, so the hydration plugin and the
 * refresh composable can stay dumb: `hydrateCollectionStore(useSitesStore(), bootstrap.collections.sites)`.
 * No-ops if `payload` is missing or the store wasn't built via defineCollectionStore.
 */
export function hydrateCollectionStore(store: any, payload: { data?: any[] | null; map?: Record<string, any>; meta?: { version?: string | null; syncedAt?: string | null } } | undefined | null) {
    const cfg = registry.get(store.$id);
    if (!cfg || !payload) return;
    applyCollectionResult(store, cfg, payload);
}

function buildActions(cfg: CollectionStoreConfig): Record<string, any> {
    const actions: Record<string, any> = {};

    if (cfg.archive) {
        actions[cfg.archive.toggleName] = function (this: any, archived: boolean) {
            this.archived = archived;
        };
    }

    actions[cfg.fetchName] = async function (this: any) {
        try {
            const res: any = await $fetch(cfg.endpoint);
            applyCollectionResult(this, cfg, res);
        } catch (e) {
            console.log(cfg.errorLabel, e);
        }
    };

    if (cfg.archive) {
        const archivedErrorLabel = cfg.archive.errorLabel ?? cfg.errorLabel;
        actions[cfg.archive.archivedFetchName] = async function (this: any, year: any) {
            try {
                const url = cfg.archive!.archivedEndpoint(year);
                const res: any = await $fetch(url);
                this[cfg.archive!.archivedDataKey] = cfg.wrapRecords ? { records: res?.data } : cfg.dataFallback !== undefined ? res?.data || cfg.dataFallback : res?.data;

                const targetMapKey = cfg.archive!.archivedMapKey ?? cfg.mapKey;
                if (targetMapKey) {
                    // Derived from `data`, as applyCollectionResult does: the archive endpoints no longer send a map.
                    this[targetMapKey] = Array.isArray(res?.data) ? makeAPIMap(res.data) : cfg.mapFallback;
                }
                if (cfg.trackLastFetched) this.lastFetched = Date.now();
            } catch (e) {
                console.log(archivedErrorLabel, e);
            }
        };
    }

    if (cfg.byId) {
        actions[cfg.byId.actionName] = async function (this: any, id: string, ...rest: any[]) {
            try {
                const url = cfg.byId!.endpoint(id, ...rest);
                const res: any = await $fetch(url);
                if (cfg.mapKey) this[cfg.mapKey][id] = res?.data;
                return res?.response;
            } catch (e) {
                console.log(cfg.errorLabel, e);
            }
        };
    }

    Object.assign(actions, cfg.extraActions ?? {});

    return actions;
}

export const defineCollectionStore = (cfg: CollectionStoreConfig) => {
    registry.set(cfg.id, cfg);
    return defineStore(cfg.id, {
        state: () => buildState(cfg),
        getters: buildArchiveGetters(cfg),
        actions: buildActions(cfg),
    });
};
